import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Home, Sparkles, User, Mail, Lock } from 'lucide-react';

interface RegisterProps {
  onSwitchToLogin: () => void;
}

const Register: React.FC<RegisterProps> = ({ onSwitchToLogin }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();

  const handleSubmit = async () => {
    if (!name || !email || !password) {
      setError('Please fill all fields');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await register(name, email, password);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-600 via-teal-500 to-green-700 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-72 h-72 bg-green-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-teal-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-40 w-72 h-72 bg-emerald-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative z-10 backdrop-blur-sm bg-opacity-95">
        {/* Glowing Header */}
        <div className="text-center mb-8 relative">
          <div className="flex justify-center mb-4 relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <Sparkles className="w-20 h-20 text-teal-300 animate-pulse" />
            </div>
            <Home className="w-16 h-16 text-green-600 relative z-10 animate-bounce" style={{ animationDuration: '2s' }} />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-green-700 via-teal-600 to-emerald-600 bg-clip-text text-transparent mb-2">
            🏘️ Bhoomi
          </h1>
          <p className="text-gray-600 font-medium">Join Hybrid Land Ownership</p>
          <div className="flex items-center justify-center space-x-1 mt-2">
            <div className="w-2 h-2 bg-green-600 rounded-full animate-ping"></div>
            <div className="w-2 h-2 bg-teal-500 rounded-full animate-ping animation-delay-200"></div>
            <div className="w-2 h-2 bg-emerald-600 rounded-full animate-ping animation-delay-400"></div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg mb-4 animate-shake">
            <p className="font-semibold">Error</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div className="relative">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
              <User className="w-4 h-4 text-green-600" />
              <span>Full Name</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all duration-200 hover:border-green-300 pl-4"
              placeholder="Enter your full name"
            />
          </div>

          <div className="relative">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
              <Mail className="w-4 h-4 text-green-600" />
              <span>Email Address</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all duration-200 hover:border-green-300"
              placeholder="your@email.com"
            />
          </div>

          <div className="relative">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
              <Lock className="w-4 h-4 text-green-600" />
              <span>Password</span>
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all duration-200 hover:border-green-300"
              placeholder="Create a strong password"
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-gradient-to-r from-green-600 via-teal-500 to-emerald-600 text-white py-3 rounded-xl font-bold text-lg hover:from-green-700 hover:via-teal-600 hover:to-emerald-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating account...
              </span>
            ) : (
              '✨ Create Account'
            )}
          </button>

          {/* Bonus Message */}
          <div className="bg-gradient-to-r from-green-50 to-teal-50 border-2 border-green-200 rounded-xl p-4 text-center">
            <p className="text-green-800 font-semibold text-sm">
              🎁 New users start with <span className="text-lg">100 URJA</span> bonus!
            </p>
          </div>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={onSwitchToLogin}
            className="text-green-700 hover:text-green-800 text-sm font-semibold hover:underline transition-all"
          >
            Already have an account? <span className="text-teal-600">Sign In</span>
          </button>
        </div>

        {/* Decorative Elements */}
        <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-green-500 to-teal-600 rounded-full opacity-20 blur-2xl"></div>
        <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full opacity-20 blur-2xl"></div>
      </div>

      {/* Add custom CSS for animations */}
      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        .animate-shake {
          animation: shake 0.5s;
        }
      `}</style>
    </div>
  );
};

export default Register;
